import { SubmitHandler } from "react-hook-form";
import { useUserWizardForm } from "./useUserWizardPasswordForm";
import { IUserWizardFormModel } from "../models/userWizardFormModel";
import { endpointsMetadata } from "../../../../../common/infrastructure/routes/endpointsMetadata";
import { useAppContext } from "../../../../../AppContext";
import { useNavigate, useParams } from "react-router-dom";
import {
  constructUserHref,
  routePaths,
} from "../../../../../common/infrastructure/routes/routePaths";
import { PageMode } from "../../../../../common/types";
import { useApiQuery } from "../../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../../common/infrastructure/hooks/useApiMutation";
import { normalizePhone } from "../../../../../common/components/forms/inputs/phoneField/utils/PhoneInputUtility";
import { IOperationResultViewModel } from "../../../../../common/models/operationResultViewModel";
import { useState } from "react";

export const useUserWizardPage = (
  pageMode: PageMode,
  onCreated?: (newUserId: number, newUserName: string) => void,
  endpoint?: string
) => {
  const [isCreateRequestLoading, setIsCreateRequestLoading] = useState(false);
  const { selectedStudioId, isDesktopOrTablet, showGroupTypeOptions } =
    useAppContext();

  const { mutateAsync } = useApiMutation();
  const navigate = useNavigate();
  const { id } = useParams();

  const { data: client, isLoading } = useApiQuery<IUserWizardFormModel>({
    queryKey: [endpointsMetadata.user.wizardGetClient, id],
    endpoint: `${endpointsMetadata.user.wizardGetClient}?clientId=${id}`,
    enabled: id !== undefined,
  });

  const actionButtonText = pageMode === "add" ? "Добавяне" : "Запазване";

  const form = useUserWizardForm(selectedStudioId, client);

  const handleSubmit: SubmitHandler<IUserWizardFormModel> = async (data) => {
    setIsCreateRequestLoading(true);
    if (data.phoneNumber) {
      data.phoneNumber = normalizePhone(data.phoneNumber);
    }

    // Same "First Last" label the server gives the user in client dropdowns.
    const newUserName = `${data.firstName} ${data.lastName}`;

    try {
      await mutateAsync(
        {
          endpoint: endpoint ?? endpointsMetadata.user.wizard,
          globalIsLoading: false,
          method: "post",
          invalidateKeys: [
            [endpointsMetadata.planner.getAvailableEventUsersWihoutDeleted],
            [endpointsMetadata.planner.wizardGetInitialData],
            [endpointsMetadata.user.getAll],
            [endpointsMetadata.user.wizardGetClient],
          ],
          ...data,
        },
        {
          onSuccess: (data) => {
            const result = data as IOperationResultViewModel<number>;
            if (onCreated) {
              onCreated(Number(result.additionalData), newUserName);
              return;
            }

            if (!!result.additionalData) {
              navigate(constructUserHref(Number(result.additionalData)));
            } else {
              navigate(routePaths.users);
            }
          },
        }
      );
    } catch {
    } finally {
      setIsCreateRequestLoading(false);
    }
  };

  return {
    id,
    form,
    isLoading: isLoading && id !== undefined,
    actionButtonText,
    client,
    isCreateRequestLoading,
    pageMode,
    isDesktopOrTablet,
    showGroupTypeOptions,
    navigate,
    handleSubmit,
  };
};
